import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, colors, InputAdornment, Stack, Typography } from '@mui/material';
import { Add as AddIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import ContentEditor from 'components/ContentEditor';
import { TitleTextField } from '../styles';
import { ActionMenu } from 'components/_atoms';
import {
  DraftManual,
  DraftPolicy,
  DraftPolicySection,
  DraftPolicySubSection,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import SectionTags from '../PolicyEditor/SectionTags';
import policyComparer from '../PolicyEditor/PolicyCompare/comparer';
import { usePolicyEditorContext } from '../PolicyEditor/context';
import ContentConversionHelper from '../../helpers/contentConversionHelper';
import AttachmentBox from '../AttachmentLibrary/_components/AttachmentBox';
import AttachmentLibrary from '../AttachmentLibrary';
import {
  cleanupTrackChangesForStorage,
  removeDeletedNodes,
  unWrapInsertedNodes,
} from '../../helpers/documentTransformations';
import { translate as t } from 'hooks/useTranslations';

type SectionEditorProps = {
  section: DraftPolicySection | DraftPolicySubSection;
  sectionNumber: string;
  sectionId: string;
  isEditable?: boolean;
  isDisabled?: boolean;
  isSubSection?: boolean;
  isLastItem?: boolean;
  viewOnly?: boolean;
  manual: DraftManual;
  issuedAt?: string;
};

const SectionEditor: React.FC<SectionEditorProps> = React.memo(
  ({
    section,
    sectionId,
    sectionNumber,
    isEditable = false,
    isDisabled = false,
    isSubSection = false,
    isLastItem = false,
    viewOnly = true,
    issuedAt,
    manual,
  }: SectionEditorProps) => {
    const {
      committedPolicyQuery,
      handleInsertSectionAfter,
      handleInsertSubSectionAfter,
      handleSectionChange,
      handleSelectSection,
      policyNumber,
      policyData,
      addAttachmentsToSection,
    } = usePolicyEditorContext();
    const confirm = useConfirm();
    const [sectionData, setSectionData] = useState<DraftPolicySection | DraftPolicySubSection>(section);
    const [showAddingSection, setShowAddingSection] = useState<boolean>(false);
    const [showAddingSubsection, setShowAddingSubsection] = useState<boolean>(false);
    const [agencyModified, setAgencyModified] = useState<boolean>(false);
    const lastContentRef = useRef<string | null>(null);

    const handleSelectAttachments = useCallback(
      (selectedAttachments: { id: string; embedded: boolean }[]) => {
        addAttachmentsToSection(sectionData.id, selectedAttachments);
      },
      [addAttachmentsToSection, sectionData.id]
    );

    // console.log('section.content', [section.content, section.contentEditable]);
    // console.log('sectionData.content', [sectionData.content, sectionData.contentEditable]);

    useEffect(() => {
      setSectionData((prev) => ({ ...prev, attachments: section.attachments }));
    }, [section.attachments]);

    const updateSectionData = (
      updatedData: Partial<DraftPolicySection | DraftPolicySubSection>,
      triggerChange = true
    ) => {
      const newSectionData = { ...sectionData, ...updatedData };
      setSectionData(newSectionData);

      if (triggerChange) {
        if (updatedData.title && !updatedData.contentEditable) {
          const titleOnlyUpdate = {
            ...newSectionData,
            content: section.content,
            contentEditable: section.contentEditable,
          };
          handleSectionChange(titleOnlyUpdate);
        } else {
          handleSectionChange(newSectionData);
        }
      }
    };

    const handleTitleChange = (newTitle: string) => {
      updateSectionData({ title: newTitle });
    };

    const handleContentChange = (content: string) => {
      lastContentRef.current = content;

      // Extract attachment IDs from content
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      const imgElements = doc.querySelectorAll('img[data-attachment-id]');
      const attachmentIds = Array.from(imgElements)
        .map((img) => img.getAttribute('data-attachment-id'))
        .filter((id): id is string => id !== null);

      // Filter out embedded attachments that don't have corresponding images
      const existingAttachments = (sectionData.attachments || []).filter((att) => {
        if (att.embedded) {
          return attachmentIds.includes(att.id);
        }
        return true;
      });

      // Update section data with content and ensure attachments are tracked
      // content should NEVER have track changes - only contentEditable should have them
      // content is used for publishing to issued manuals, so it must be clean
      //
      // Clean up track changes based on issuedAt when saving contentEditable
      // This ensures stored content is always clean relative to the issue date
      const cleanedContentEditable = cleanupTrackChangesForStorage(content, issuedAt);

      updateSectionData({
        content: ContentConversionHelper.contentForRender(content, {
          issuedAt,
          // Remove both insert and delete nodes from content (they should only exist in contentEditable)
          additionalTransformations: [unWrapInsertedNodes, removeDeletedNodes],
        }),
        contentEditable:
          sectionData.content === ContentConversionHelper.contentForEditor(content)
            ? sectionData.content
            : cleanedContentEditable,
        attachments: [
          ...existingAttachments,
          ...attachmentIds
            .filter((id) => !existingAttachments.some((att) => att.id === id))
            .map((id) => ({ id, embedded: true })),
        ],
      });
    };

    const handleRemoveAttachment = (attachmentId: string) => {
      const updatedAttachments = (sectionData.attachments || []).filter((att: any) => att.id !== attachmentId);
      const updatedSection = { ...sectionData, attachments: updatedAttachments };
      updateSectionData(updatedSection);
      handleSectionChange(updatedSection);
    };

    const handleAddSectionClick = () => {
      confirm({
        title: t('draftManuals.addConfirmation.section.title'),
        description: t('draftManuals.addConfirmation.section.message'),
        confirmationText: t('draftManuals.addConfirmation.section.confirm'),
        cancellationText: t('draftManuals.addConfirmation.cancel'),
        confirmationButtonProps: {
          variant: 'contained',
        },
        cancellationButtonProps: {
          variant: 'outlined',
          color: 'neutral',
        },
      })
        .then(() => {
          handleInsertSectionAfter(section.id);
        })
        .catch(() => {
          // User cancelled
        });
    };

    const handleAddSubsectionClick = () => {
      confirm({
        title: t('draftManuals.addConfirmation.subsection.title'),
        description: t('draftManuals.addConfirmation.subsection.message'),
        confirmationText: t('draftManuals.addConfirmation.subsection.confirm'),
        cancellationText: t('draftManuals.addConfirmation.cancel'),
        confirmationButtonProps: {
          variant: 'contained',
        },
        cancellationButtonProps: {
          variant: 'outlined',
          color: 'neutral',
        },
      })
        .then(() => {
          handleInsertSubSectionAfter(sectionId, isSubSection ? section.id : undefined);
        })
        .catch(() => {
          // User cancelled
        });
    };

    const prevSectionData = useRef<DraftPolicySection | DraftPolicySubSection>(section);
    useEffect(() => {
      if (sectionData && JSON.stringify(sectionData) !== JSON.stringify(prevSectionData.current)) {
        prevSectionData.current = sectionData;
        if (manual.manualLevel === 'AGENCY') {
          let agencyModified = sectionData.agencyModified;
          if (sectionData.editLevelTag !== 'AGENCY_CONTENT') {
            let committedSection: DraftPolicySection | DraftPolicySubSection | undefined;
            if (committedPolicyQuery.data?.sections) {
              committedPolicyQuery.data.sections.forEach((s: DraftPolicySection) => {
                if (s.id === sectionData.id) {
                  committedSection = s;
                }
                s.subSections.forEach((ss) => {
                  if (ss.id === sectionData.id) {
                    committedSection = ss;
                  }
                });
              });
            }
            if (committedSection) {
              const diffs = policyComparer.compareNodes(sectionData, committedSection);
              if (diffs.length > 0) {
                agencyModified = true;
              }
            }
          }
          setAgencyModified(agencyModified);
        }
      }
    }, [sectionData, manual, committedPolicyQuery.data, handleSectionChange]);

    return sectionData ? (
      <Box
        id={`section_${sectionData.id}`}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ml: isSubSection ? 5 : 0,
          '& .section-footer': { opacity: 0 },
          '&:hover .section-footer': { opacity: 1 },
        }}
      >
        <SectionTags
          section={sectionData}
          editable={manual.manualLevel !== 'AGENCY' && sectionData.editLevelTag !== 'AGENCY_CONTENT'}
          agencyModified={agencyModified}
          policyData={policyData}
          manual={manual}
          handleEditLevelTagChange={(newEditLevel) =>
            updateSectionData({
              editLevelTag: newEditLevel as
                | 'FEDERAL'
                | 'STATE'
                | 'BEST_PRACTICE'
                | 'DISCRETIONARY'
                | 'AGENCY_CONTENT'
                | 'ACCREDITATION'
                | undefined,
            })
          }
        />
        <Box>
          <Box
            className={isEditable ? 'phx-toolbar-edit' : 'phx-toolbar-view'}
            sx={{ gap: 4, borderRadius: '4px !important', display: 'flex' }}
          >
            <>
              {isEditable ? (
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{ flexGrow: 1, alignItems: 'center' }}
                >
                  <TitleTextField
                    id={`title_${sectionData.id}`}
                    key={`title_${sectionData.id}`}
                    label={false}
                    variant='outlined'
                    fullWidth
                    value={
                      sectionData?.title
                        ?.replace(/<[^>]*>/g, '')
                        ?.replace(/&amp;/g, '&')
                        ?.replace(/&lt;/g, '<')
                        ?.replace(/&gt;/g, '>')
                        ?.replace(/&quot;/g, '"')
                        ?.replace(/&#39;/g, "'") ?? ''
                    }
                    onChange={({ target }) => handleTitleChange(target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position='start'
                          sx={{ '& .MuiTypography-root': { fontSize: 20, fontWeight: 600 } }}
                        >
                          {sectionNumber}
                        </InputAdornment>
                      ),
                    }}
                    autoFocus
                  />
                </Stack>
              ) : (
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{
                    flexGrow: 1,
                    alignItems: 'center',
                    '&:hover .edit-title-btn': { opacity: 1 },
                  }}
                >
                  <Typography
                    className={`title_${sectionData.id}`}
                    sx={{ fontSize: 16, fontWeight: 600, opacity: !isDisabled ? 1 : 0.5 }}
                    onMouseDown={() => {
                      if (!isDisabled) handleSelectSection(sectionData.id);
                    }}
                  >
                    {!isDisabled
                      ? sectionNumber +
                        ': ' +
                        (sectionData?.title
                          ?.replace(/<[^>]*>/g, '')
                          ?.replace(/&amp;/g, '&')
                          ?.replace(/&lt;/g, '<')
                          ?.replace(/&gt;/g, '>')
                          ?.replace(/&quot;/g, '"')
                          ?.replace(/&#39;/g, "'") ?? '')
                      : (sectionData?.title
                          ?.replace(/<[^>]*>/g, '')
                          ?.replace(/&amp;/g, '&')
                          ?.replace(/&lt;/g, '<')
                          ?.replace(/&gt;/g, '>')
                          ?.replace(/&quot;/g, '"')
                          ?.replace(/&#39;/g, "'") ?? '')}
                  </Typography>
                </Stack>
              )}
            </>
          </Box>
        </Box>
        <Box
          sx={{ p: 0, minHeight: 0, overflow: 'auto', opacity: !isDisabled ? 1 : 0.5 }}
          onMouseDown={() => {
            if (!isDisabled) handleSelectSection(sectionData.id);
          }}
        >
          <ContentEditor
            id={`content_${sectionData.id}`}
            key={`content_${sectionData.id}_${isEditable}`}
            contentHTML={sectionData.contentEditable ?? ''}
            onSave={handleContentChange}
            isEditable={isEditable && !viewOnly}
            trackChangesEnabled={true}
            textEntitiesEnabled={true}
            crossReferencesEnabled={manual.manualLevel === 'AGENCY'}
            imagesEnabled={true}
            attachmentsEnabled={true}
            manualId={manual.id}
            crossReferences={sectionData.crossReferences}
            onSelectAttachments={handleSelectAttachments}
            AttachmentLibraryComponent={AttachmentLibrary}
          />
        </Box>

        {sectionData.attachments && sectionData.attachments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack
              direction='row'
              spacing={1}
            >
              {sectionData.attachments
                .filter((att: any) => !att.embedded)
                .map((att: any) => (
                  <AttachmentBox
                    key={att.id}
                    attachmentRef={att}
                    manualId={manual.id}
                    onRemove={() => handleRemoveAttachment(att.id)}
                    isEditable={true}
                  />
                ))}
            </Stack>
          </Box>
        )}
        <Stack spacing={2}>
          <Box
            className='section-footer'
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {viewOnly ? (
              <Box></Box>
            ) : (
              <>
                <Box
                  sx={{
                    border: `solid 1px ${colors.grey[200]}`,
                    borderRadius: 1,
                    px: 3,
                    py: 0,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, auto)',
                    alignItems: 'center',
                  }}
                >
                  {!isDisabled && (
                    <>
                      {(!isSubSection || (isSubSection && isLastItem)) && (
                        <>
                          <Button
                            variant='text'
                            size='small'
                            startIcon={<AddIcon />}
                            color='neutral'
                            sx={{ whiteSpace: 'nowrap' }}
                            onMouseOver={() => setShowAddingSection(true)}
                            onMouseOut={() => setShowAddingSection(false)}
                            onClick={handleAddSectionClick}
                          >
                            Section
                          </Button>
                          <Box sx={{ background: colors.grey[300], width: 2, height: 16 }} />
                        </>
                      )}
                      <Button
                        variant='text'
                        size='small'
                        startIcon={<AddIcon />}
                        color='neutral'
                        sx={{ whiteSpace: 'nowrap' }}
                        onMouseOver={() => setShowAddingSubsection(true)}
                        onMouseOut={() => setShowAddingSubsection(false)}
                        onClick={handleAddSubsectionClick}
                      >
                        Sub-section
                      </Button>
                    </>
                  )}
                </Box>
              </>
            )}
            <Box
              sx={{
                border: `solid 1px ${colors.grey[200]}`,
                borderRadius: 1,
                px: 0,
                py: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(1, auto)',
                alignItems: 'center',
              }}
            >
              <Button
                variant='text'
                size='small'
                color='neutral'
                sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
                onClick={() => {
                  updateSectionData({
                    state: sectionData.state === 'ENABLED' ? 'DISABLED' : 'ENABLED',
                  });
                }}
              >
                {sectionData.state === 'ENABLED' ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </Button>
            </Box>
          </Box>
          {showAddingSection && (
            <Box
              sx={{
                height: 16,
                marginLeft: isSubSection ? '-20px !important' : '',
                width: '100%',
                background: colors.blueGrey[50],
                border: `dotted 2px ${colors.blueGrey[200]}`,
              }}
            ></Box>
          )}
          {showAddingSubsection && (
            <Box
              sx={{
                height: 16,
                marginLeft: isSubSection ? '' : '20px !important',
                width: 'calc(100% - 20px)',
                background: colors.blueGrey[50],
                border: `dotted 2px ${colors.blueGrey[200]}`,
              }}
            ></Box>
          )}
        </Stack>
      </Box>
    ) : null;
  }
);

export default SectionEditor;
